import { mapActions, mapGetters } from 'vuex'
import countries from '@/utils/_countries.json'
import languages from '@/utils/_languages.json'
import keywordExclusionRegex from '@/utils/keywordExclusionRegex'

export default {
  name: 'FreeClusterer',
  data () {
    return {
      isLoading: {
        clusterer: false,
        user: false,
        email: false
      },

      keywordLimit: 200,
      keywords: null,
      keywordRules: [
        value => !value || value?.split(/[\r\n\t]+/)?.length <= this.keywordLimit || 'Too many keywords',
        value => !value || value?.split(/[\r\n\t]+/)?.length > 1 || 'Enter at least 2 keywords'
      ],
      domain: null,
      domainRules: [
        // value can be empty
        value => !value || /^(https?:\/\/)?(www\.)?([a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]\.)+[a-zA-Z]{2,}\/?$/.test(value) || 'Invalid domain'
      ],
      email: null,
      emailSent: false,
      emailSaved: false,
      emailRules: [
        value => !value || /.+@.+\..+/.test(value) || 'Invalid email'
      ],

      form: null,
      countries,
      country: 'USA',
      languages,
      language: 'en',
      serpOverlap: 2,

      contentplanId: null,
      workspaceId: null,
      step: 0,
      csvUrl: null,
      socket: null
    }
  },
  computed: {
    ...mapGetters([
      'websocketUrl'
    ]),
    keywordsHint () {
      const keywordsArray = this.keywords?.split(/[\r\n\t]+/)?.filter(k => k)
      if (!keywordsArray?.length) return 'Enter a list of keywords, one per line'
      if (/,+/u.test(this.keywords)) return 'Enter one keyword per line. Don\'t use commas to seperate keywords'
      if (keywordsArray.some(k => new RegExp(keywordExclusionRegex.regex).test(k))) return 'Keywords with punctuation or special symbols will be ignored'
      return ''
    },
    signUpURI () {
      if (!this.contentplanId) return ''
      return `/auth/in?contentplanId=${this.contentplanId}`
    },
    isRusLocation () {
      return this.country === 'RUS' || this.country === 'BLR'
    },
    isRusDomain () {
      if (!this.form) return false
      return /(ru|by)\/?$/u.test(this.domain)
    },
    hasCleanKeywords () {
      const keywordsArray = this.keywords?.split(/[\r\n\t]+/)?.filter(k => k)
      if (!keywordsArray?.length) return true
      const keywordsCleaned = keywordsArray?.map(k => k.trim().toLowerCase()).filter(k => !!k).filter(k => {
        return !new RegExp(keywordExclusionRegex.regex).test(k)
      })

      return keywordsCleaned && keywordsCleaned.length > 0
    },
    errorMessage () {
      if (this.isRusLocation) return 'We don\'t support Russia or Belarus.'
      if (!this.hasCleanKeywords) return 'Please enter keywords without punctuation or special symbols'
      if (this.isRusDomain) return 'We don\'t support Russian or Belarussian domains.'
      return null
    }
  },
  watch: {
  },
  methods: {
    ...mapActions([
      'postUser',
      'sendEmail'
    ]),
    countKeywords () {
      if (!this.keywords?.length) return 0
      // count keywords one per line
      return this.keywords?.split(/[\r\n\t]+/)?.filter(k => k).length || 0
    },
    async runClusterer () {
      try {
        if (!this.keywords?.length) return
        this.isLoading.clusterer = true

        // Turn this.keywords string into array
        const keywords = this.keywords.split(/[\r\n\t]+/)?.filter(k => k?.length)

        // Start websocket connection and trigger clustering
        await this.connectWebSocketAndTriggerClustering(keywords)
      } catch (err) {
        console.log(err)
        this.isLoading.clusterer = false
      }
    },
    async connectWebSocketAndTriggerClustering (keywords) {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        console.log('WebSocket connection not established, opening...')
        this.socket = new WebSocket(this.websocketUrl)
      }

      this.socket.onopen = () => {
        console.log('WebSocket connection established')
        // Trigger the clusterer via websocket
        this.sendMessage({
          action: 'runClusterer',
          message: {
            keywords,
            country: this.country,
            language: this.language,
            serpOverlap: this.serpOverlap,
            domain: this.domain,
            type: 'saas-cluster'
          }
        })
      }

      // Handle incoming messages
      this.socket.onmessage = (event) => {
        console.log('Message received:', event)
        this.websocketMessageHandler(event)
      }

      this.socket.onerror = (error) => {
        console.error('WebSocket error: ', error.message)
      }

      this.socket.onclose = () => {
        console.log('WebSocket connection closed')
      }
    },
    async sendMessage (body) {
      try {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
          console.log('WebSocket connection not established, opening...')
          await this.connectWebSocketAndTriggerClustering()
        } else {
          console.log('WebSocket connection open, sending message...')
          this.socket.send(JSON.stringify(body))
        }
      } catch (err) {
        console.error(err)
      }
    },
    async websocketMessageHandler (event) {
      const eventData = JSON.parse(event.data)
      const messages = eventData?.messages
      if (!messages) return
      const messageData = messages[0]
      if (!messageData) return
      const { stepName, status, contentplanId, csvUrl } = messageData

      console.log('WebSocket message:', { stepName, status, contentplanId, csvUrl })

      // Capture contentplanId from first message
      if (contentplanId && !this.contentplanId) {
        this.contentplanId = contentplanId
        this.workspaceId = 1 // Free clustering uses workspace 1
        this.step = 1 // Started
      }

      // Handle step transitions from state machine
      if (stepName === 'Crawling' && status === 'done') {
        this.step = 2 // Crawling complete, moving to grouping
      }

      if (stepName === 'Clustering' && status === 'done') {
        this.step = 3 // Grouping complete
      }

      if (stepName === 'CreateCSV' && status === 'done' && csvUrl) {
        this.csvUrl = csvUrl
        this.step = 4
        this.isLoading.clusterer = false
      }

      // Handle errors
      if (status === 'error') {
        this.isLoading.clusterer = false
      }
    },
    reset () {
      this.keywords = null
      this.country = 'USA'
      this.language = 'en'
      this.step = null
      this.csvUrl = null
      this.contentplanId = null
      this.workspaceId = null
      this.emailSent = false
      this.emailSaved = false
      if (this.socket) {
        this.socket.close()
        this.socket = null
      }
    },
    async addNewUser () {
      this.isLoading.user = true
      console.log('adding new user')

      let userId
      try {
        userId = await this.postUser({
          contentplanId: this.contentplanId,
          email: this.email
        })
      } catch (err) {
        console.log(err)
      }

      this.emailSaved = true
      this.isLoading.user = false

      return userId
    },
    async addNewUserAndSendEmail () {
      this.isLoading.email = true

      try {
        const userId = await this.addNewUser()
        console.log('userId', userId)
        await this.sendEmail({
          action: 'clusterer',
          userId,
          contentplanId: this.contentplanId
        })
        console.log('sent email')
      } catch (err) {
        console.log(err)
      }

      this.emailSent = true
      this.isLoading.email = false
    }
  },
  async mounted () {
  },
  beforeDestroy () {
    // Clean up websocket
    if (this.socket) {
      this.socket.close()
    }
  }
}
